import { baseApi, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import { normalizeRid } from '../../../shared/model/scopeKeys'
import { onecEnvelopeDecoder, type OneCEnvelope } from '../../../shared/api/onecEnvelope'
import { ONEC_CONTRACT_VERSION, writeCommandBody, type WriteCommandIdentity } from '../../../shared/api/writeCommand'
import { ONEC_PHOTO_SCHEMAS, decodeFileKeys, decodeMainPhotoResult } from './contracts'
import { ONEC_PHOTO_URLS } from './urls'

/** Фото товаров 1С в общем `baseApi`: имена `onec*`, rid в аргументах и тегах. */
export const ONEC_PHOTO_TAG = 'OneCProductPhoto' as const

export interface OneCProductPhotosArgs {
  resourceId: string
  refKey: string
}

export interface OneCProductsWithPhotosArgs {
  resourceId: string
  /** Отсортированы binding-ом: один набор — один ключ кэша. */
  refKeys: readonly string[]
  expectedConfigurationVersion: string
}

export interface OneCSetMainPhotoArgs {
  resourceId: string
  refKey: string
  fileKey: string
  identity: WriteCommandIdentity
}

export const photosApi = baseApi.enhanceEndpoints({ addTagTypes: [ONEC_PHOTO_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    onecProductPhotos: build.query<OneCEnvelope<string[]>, OneCProductPhotosArgs>({
      query: ({ resourceId, refKey }) => ({
        url: ONEC_PHOTO_URLS.list(resourceId, refKey),
        decode: onecEnvelopeDecoder(resourceId, decodeFileKeys),
      }),
      providesTags: (_result, _error, { resourceId, refKey }) => [
        resourceTag(ONEC_PHOTO_TAG, normalizeRid(resourceId), refKey),
        resourceScopeTag(normalizeRid(resourceId)),
      ],
    }),
    /** Читающий POST (право чтения): у каких товаров есть фото в кэше службы. */
    onecProductsWithPhotos: build.query<OneCEnvelope<string[]>, OneCProductsWithPhotosArgs>({
      query: ({ resourceId, refKeys, expectedConfigurationVersion }) => ({
        url: ONEC_PHOTO_URLS.present(resourceId),
        method: 'POST',
        body: {
          contractVersion: ONEC_CONTRACT_VERSION,
          schemaVersion: ONEC_PHOTO_SCHEMAS.present,
          expectedConfigurationVersion,
          data: { refKeys },
        },
        decode: onecEnvelopeDecoder(resourceId, decodeFileKeys),
      }),
      providesTags: (_result, _error, { resourceId }) => [
        { type: ONEC_PHOTO_TAG, id: `${normalizeRid(resourceId)}:PRESENT` },
        resourceScopeTag(normalizeRid(resourceId)),
      ],
    }),
    onecSetMainPhoto: build.mutation<OneCEnvelope<boolean>, OneCSetMainPhotoArgs>({
      query: ({ resourceId, refKey, fileKey, identity }) => ({
        url: ONEC_PHOTO_URLS.setMain(resourceId, refKey),
        method: 'POST',
        body: writeCommandBody(ONEC_PHOTO_SCHEMAS.setMain, identity, { fileKey }),
        idempotencyKey: identity.idempotencyKey,
        decode: onecEnvelopeDecoder(resourceId, decodeMainPhotoResult),
      }),
      invalidatesTags: (_result, _error, { resourceId, refKey }) => [resourceTag(ONEC_PHOTO_TAG, normalizeRid(resourceId), refKey)],
    }),
  }),
})

export const { onecProductPhotos, onecProductsWithPhotos, onecSetMainPhoto } = photosApi.endpoints
